/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
import { request } from '@core/api';
import { ApiOptions } from '@core/api/types';
import { ReduxEntities } from '@core/redux/enum';
import { crudEntityFactory, EntityRelationKey } from '@core/redux/factory';
import { getTransport } from '@core/redux/transport';
import { commonApiV1 as commonApi, scanCategoryApiV1 as scanCategoryApi } from '@deepup/apis';
import {
  iscanApiV1 as iscanApi,
  iscanClientApiV1 as iscanClientApi,
  iorganizationManagementApiV1 as iorganizationManagementApi,
} from '@deepup/internal-apis';
import { createAsyncThunk, type Action } from '@reduxjs/toolkit';
import type { SliceState } from '@core/redux/factory/interfaces';
import type { QaStateUpdate, Scan, SurfaceClassificationFeedback, QAState } from '../types';

export const {
  reducer: baseScanReducer,
  apiThunks: { getMany, ...baseScanThunks },
  actions: scanActions,
  selectors: scanSelectors,
} = crudEntityFactory<Scan>({
  entity: ReduxEntities.Scans,
  removeKeys: [
    EntityRelationKey.Project,
    EntityRelationKey.Tags,
    EntityRelationKey.ProcessingResults,
    EntityRelationKey.SelectedProcessingResult,
  ],
});

const getScansFilteredByCategory = async (input: ApiOptions) => {
  const transport = getTransport();
  const client = new iscanClientApi.InternalScanServiceClient(transport);

  const scanCategoryKind = input.params?.scanCategory as scanCategoryApi.ScanCategoryKind;

  const { items: scanInfos } = await client.listScanInfos({
    projectIds: [String(input.params?.projectId)],
    deviceIds: [],
    scanIds: [],
    surfaceClassificationLabels: [],
    qaState: iscanApi.QAState.NONE,
    scanCategoryKind,
    sortBy: iscanApi.StreamScanInfosRequest_SortBy.UNSPECIFIED,
    sortOrder: commonApi.SortOrder.ASC,
    scanCategoryKinds: [],
    qaStates: [],
    exportStatusCodes: [],
    labelingType: iorganizationManagementApi.LabelingType.UNSPECIFIED,
  }).response;

  const scanIds = scanInfos.map(({ scan }) => scan?.id);

  const { content: scans } = await request<{ content: Scan[] }>('GET', input);

  const filteredScans = scans
    .filter((scan) => scanIds.includes(scan.id))
    .map((scan) => ({ ...scan, scanCategory: scanCategoryKind }));

  return { content: filteredScans };
};

export const scanThunks = {
  ...baseScanThunks,
  searchWithFilters: baseScanThunks.create('search/withFilters', 'GET'),
  getMany: (opts: ApiOptions) => {
    if (opts.params?.scanCategory) {
      const thunk = createAsyncThunk<{ content: Scan[] }, ApiOptions>(
        'scans/api/get/many',
        async (input) => {
          return getScansFilteredByCategory(input);
        },
      );

      return thunk(opts) as unknown as ReturnType<typeof getMany>;
    }

    return getMany(opts);
  },
  updateQaState: baseScanThunks.create<QaStateUpdate[]>('qa-state/update', 'PATCH'),
  updateClassification: baseScanThunks.create<SurfaceClassificationFeedback>(
    'surface-classification/update',
    'PATCH',
  ),
};

export const scanReducer = (
  state: SliceState<Scan> | undefined,
  action: Action,
): SliceState<Scan> => {
  const nextState = baseScanReducer(state, action);
  if (scanThunks.updateQaState.fulfilled.match(action)) {
    const body = (
      action.meta.arg as {
        body?: { id: string; qaState: QAState; remarks?: string | null }[];
      }
    ).body;
    if (Array.isArray(body)) {
      const updates = body.map((item) => ({
        id: item.id,
        changes: {
          qaState: item.qaState,
          ...(item.remarks !== undefined ? { remarks: item.remarks } : {}),
        },
      }));
      return baseScanReducer(nextState, scanActions.updateMany(updates));
    }
  }
  return nextState;
};
